import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ContentIndex } from '@ezishizi/content-schema';
import { store, type Profile } from '../lib/store/index.ts';
import { useAuthSession } from '../lib/session.ts';
import { loadIndex } from '../lib/content.ts';
import { useApp } from '../state/app.ts';
import { Shizi } from '../components/mascot/Shizi.tsx';
import { Avatar, AVATAR_IDS, normalizeAvatar, type AvatarId } from '../components/avatars.tsx';
import { Screen } from '../components/ui.tsx';

/** Used until the content index has loaded, so the form never blocks on a fetch. */
const FALLBACK_LEVELS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].map((id) => ({
  id,
  lessonIds: [`${id}a-l01`],
}));

export function ProfilePicker() {
  const nav = useNavigate();
  const search = useSearch({ from: '/' });
  const activeProfile = useApp((s) => s.profile);
  const setProfile = useApp((s) => s.setProfile);
  const cachedProfiles = useApp((s) => s.profiles);
  const setCachedProfiles = useApp((s) => s.setProfiles);
  const [profiles, setProfiles] = useState<Profile[] | null>(cachedProfiles);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<AvatarId>(AVATAR_IDS[0]);
  const [levelId, setLevelId] = useState('p1');
  /** The lesson to unlock up to (`Profile.currentLessonId`): lesson 1 for a fresh start, or the
   * lesson the class has reached for a child joining mid-term. */
  const [unlockTo, setUnlockTo] = useState<string | null>(null);
  const { email } = useAuthSession();
  const [index, setIndex] = useState<ContentIndex | null>(null);

  useEffect(() => {
    (async () => {
      // The session check and the profile list are independent round trips: run them together
      // so the picker fills in one hop, not two. A 401 on the list means no session.
      const [ready, fresh] = await Promise.all([
        store.isReady(),
        store.listProfiles().catch(() => null),
      ]);
      if (!ready) {
        void nav({ to: '/auth' });
        return;
      }
      if (location.search || location.hash) {
        window.history.replaceState({}, '', '/');
      }
      // A transient failure keeps whatever was cached; with nothing cached, an empty list at
      // least offers "Add child" instead of placeholders forever.
      const list = fresh ?? (await store.listProfiles().catch(() => cachedProfiles ?? []));
      setProfiles(list);
      setCachedProfiles(list);
    })();
    void loadIndex().then(setIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);
  useEffect(() => {
    if (search.add === '1') startAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.add]);

  const levels = index ? [...index.levels].sort((a, b) => a.order - b.order) : FALLBACK_LEVELS;
  const level = levels.find((l) => l.id === levelId) ?? levels[0]!;
  /** The chosen level's lessons in order, with a placeholder per lesson until the index loads. */
  const lessons = level.lessonIds.map(
    (id, i) =>
      index?.lessons.find((l) => l.id === id) ?? {
        id,
        order: i + 1,
        title: null,
        authored: true,
      },
  );
  const unlockedLesson = lessons.find((l) => l.id === unlockTo) ?? lessons[0]!;
  const pickLevel = (id: string) => {
    setLevelId(id);
    setUnlockTo(null); // back to lesson 1 of the new level
  };

  const choose = (p: Profile) => {
    setProfile(p);
    void nav({ to: '/home' });
  };

  const startAdd = () => {
    setEditing(null);
    setName('');
    setAvatar(AVATAR_IDS[0]);
    setLevelId('p1');
    setUnlockTo(null);
    setAdding(true);
  };

  const startEdit = (p: Profile) => {
    setAdding(false);
    setEditing(p);
    setName(p.nickname);
    setAvatar(normalizeAvatar(p.avatar));
    setLevelId(p.levelId);
    setUnlockTo(p.currentLessonId);
  };

  const closeForm = () => {
    setAdding(false);
    setEditing(null);
  };

  const add = async () => {
    if (!name.trim()) return;
    const p = await store.createProfile({
      nickname: name.trim(),
      avatar,
      levelId: level.id,
      currentLessonId: unlockedLesson.id,
    });
    const next = [...(profiles ?? []), p];
    setProfiles(next);
    setCachedProfiles(next);
    choose(p);
  };

  const save = async () => {
    if (!editing || !name.trim()) return;
    const patch: Partial<Profile> = { nickname: name.trim(), avatar, levelId: level.id };
    // Only move the frontier when the parent picked a lesson (or changed level, which restarts
    // at that level's first lesson); a stored id the index no longer knows is left alone.
    const picked =
      lessons.find((l) => l.id === unlockTo) ?? (level.id !== editing.levelId ? lessons[0] : null);
    if (picked && picked.id !== editing.currentLessonId) patch.currentLessonId = picked.id;
    const updated = await store.updateProfile(editing.id, patch);
    const next = (profiles ?? []).map((p) => (p.id === updated.id ? updated : p));
    setProfiles(next);
    setCachedProfiles(next);
    if (activeProfile?.id === updated.id) setProfile(updated);
    closeForm();
  };

  return (
    <Screen className="items-center justify-center gap-8">
      <div className="text-center">
        <div className="flex justify-center">
          <Shizi size={120} pose="idle" />
        </div>
        <h1 className="display text-5xl">Ezi Shizi</h1>
        <p className="text-muted">Who is learning today?</p>
      </div>
      <div className="grid grid-cols-2 gap-6 landscape:grid-cols-4">
        {profiles === null &&
          // Still fetching and nothing cached: placeholders, never an empty "Add child" screen.
          [0, 1].map((i) => (
            <div
              key={i}
              className="tile flex-col gap-2 p-6 animate-pulse"
              data-testid="profile-skeleton"
              aria-hidden
            >
              <div className="h-[72px] w-[72px] rounded-full bg-line" />
              <div className="h-6 w-24 rounded-full bg-line" />
            </div>
          ))}
        {profiles?.map((p) => (
          <div
            key={p.id}
            role="button"
            tabIndex={0}
            className="tile relative flex-col gap-2 p-6"
            data-testid={`profile-${p.id}`}
            onClick={() => choose(p)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                choose(p);
              }
            }}
          >
            <button
              type="button"
              aria-label={`Edit ${p.nickname}`}
              data-testid={`edit-profile-${p.id}`}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-base shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                startEdit(p);
              }}
            >
              ✎
            </button>
            <Avatar id={normalizeAvatar(p.avatar)} size={72} />
            <span className="text-2xl font-semibold">{p.nickname}</span>
          </div>
        ))}
        <button
          type="button"
          className={`tile flex-col gap-2 p-6 border-dashed border-black/20 ${profiles === null ? 'invisible' : ''}`}
          data-testid="add-profile"
          onClick={startAdd}
        >
          <span className="text-6xl">＋</span>
          <span className="text-xl text-muted">Add child</span>
        </button>
      </div>
      {(adding || editing) && (
        <form
          className="flex flex-col items-center gap-4 rounded-3xl bg-white p-6 shadow-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void (editing ? save() : add());
          }}
        >
          <div className="flex gap-2">
            {AVATAR_IDS.map((a) => (
              <button
                key={a}
                type="button"
                className={`flex h-16 w-16 items-center justify-center rounded-2xl ${a === avatar ? 'bg-brand-soft ring-4 ring-brand' : 'bg-paper'}`}
                onClick={() => setAvatar(a)}
              >
                <Avatar id={a} size={48} />
              </button>
            ))}
          </div>
          <input
            className="rounded-2xl border-2 border-black/10 px-4 py-3 text-2xl"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="nickname"
            autoFocus
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-muted">Primary level</span>
            <div className="flex flex-wrap justify-center gap-2 landscape:flex-nowrap">
              {levels.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  disabled={!index}
                  className={`min-h-[3.5rem] min-w-[4.5rem] rounded-2xl text-xl font-semibold ${l.id === levelId ? 'bg-brand-soft ring-4 ring-brand' : 'bg-paper'}`}
                  data-testid={`level-${l.id}`}
                  onClick={() => pickLevel(l.id)}
                >
                  {l.id.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="flex max-w-[34rem] flex-col items-center gap-2">
            <span className="text-muted">Unlock lessons up to</span>
            <div
              className="flex flex-wrap justify-center gap-1.5"
              role="radiogroup"
              aria-label="Unlock lessons up to"
            >
              {lessons.map((l) => {
                const picked = l.id === unlockedLesson.id;
                const within = l.order < unlockedLesson.order;
                return (
                  <button
                    key={l.id}
                    type="button"
                    role="radio"
                    aria-checked={picked}
                    aria-label={`Lesson ${l.order}${l.authored ? '' : ', no content yet'}`}
                    title={
                      l.title ? `${l.title.zh} · ${l.title.en}` : l.authored ? '' : 'No content yet'
                    }
                    disabled={!index}
                    className={`min-h-[2.75rem] min-w-[2.75rem] rounded-xl px-2 text-lg font-semibold ${
                      picked
                        ? 'bg-brand text-white ring-4 ring-brand-soft'
                        : within
                          ? 'bg-brand-soft text-brand'
                          : 'bg-paper'
                    } ${l.authored ? '' : 'opacity-50'}`}
                    data-testid={`unlock-${l.id}`}
                    onClick={() => setUnlockTo(l.id)}
                  >
                    {l.order}
                  </button>
                );
              })}
            </div>
            <p className="text-center text-sm text-muted" data-testid="unlock-summary">
              {unlockedLesson.order === 1 ? (
                <>Starting from the beginning. Joining mid-term? Pick the lesson the class is on.</>
              ) : (
                <>
                  Lessons 1 to {unlockedLesson.order} will be open
                  {unlockedLesson.title && (
                    <>
                      {' '}
                      · <span className="hanzi">{unlockedLesson.title.zh}</span> ·{' '}
                      {unlockedLesson.title.en}
                    </>
                  )}
                  . Finishing lesson {unlockedLesson.order} unlocks the next one.
                </>
              )}
            </p>
          </div>
          {editing ? (
            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary" data-testid="save-profile">
                Save
              </button>
              <button type="button" className="btn btn-ghost" onClick={closeForm}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="submit" className="btn btn-primary" data-testid="create-profile">
              Start
            </button>
          )}
        </form>
      )}
      {store.kind === 'local' && (
        <p className="text-sm text-muted">Prototype: progress is saved on this device only.</p>
      )}
      {store.kind !== 'local' && (
        <p className="text-sm text-muted">
          {email ? `Signed in as ${email}` : 'Signed in'} ·{' '}
          <button
            type="button"
            className="underline"
            data-testid="sign-out"
            onClick={() => {
              void (async () => {
                await store.signOut();
                setCachedProfiles(null);
                void nav({ to: '/' });
              })();
            }}
          >
            Sign out
          </button>
        </p>
      )}
    </Screen>
  );
}
